import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  useTheme,
  Chip,
  Alert,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Tooltip,
  IconButton,
} from "@mui/material";
import {
  Warning as WarningIcon,
  FitnessCenter as FitnessCenterIcon,
  CheckCircle as CheckIcon,
  People as PeopleIcon,
  Star as StarIcon,
  ShowChart as ShowChartIcon,
} from "@mui/icons-material";
import MetricCard from "../shared/MetricCard";
import dashboardService from "../../../../../services/dashboardService";

const TIER_COLOR = {
  Excellent:          "success",
  Good:               "primary",
  Average:            "warning",
  "Needs Improvement":"error",
  "No Progress Data": "default",
};

const RISK_COLOR = {
  HIGH_ENGAGEMENT:  "success",
  STABLE:           "default",
  HIGH_MAINTENANCE: "warning",
  INACTIVE_PROFILE: "warning",
  SILENT:           "error",
  NON_COMMUNICATOR: "error",
  CHURNED:          "error",
  COACH_INSTABILITY:"warning",
  UNKNOWN:          "default",
};

function TierChip({ tier }) {
  return (
    <Chip
      label={tier || "—"}
      size="small"
      color={TIER_COLOR[tier] || "default"}
      variant={tier === "No Progress Data" ? "outlined" : "filled"}
      sx={{ fontSize: "0.7rem" }}
    />
  );
}

function FlagBadges({ flags }) {
  const items = [];
  if (flags.noWorkout)  items.push({ label: "No workout 14d+", color: "error" });
  if (flags.noMessage)  items.push({ label: "No message 7d+",  color: "warning" });
  if (flags.lowReports) items.push({ label: "Low reports",     color: "warning" });
  if (flags.noProtocol) items.push({ label: "No protocol",     color: "error" });
  if (!items.length) return null;
  return (
    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
      {items.map(f => (
        <Chip key={f.label} label={f.label} size="small" color={f.color} variant="outlined" sx={{ fontSize: "0.65rem", height: 20 }} />
      ))}
    </Box>
  );
}

const SORT_FIELDS = {
  name:             (c) => `${c.lastName} ${c.firstName}`.toLowerCase(),
  performanceScore: (c) => c.performanceScore ?? -1,
  completionPct:    (c) => c.completionPct    ?? -1,
  daysSinceWorkout: (c) => c.daysSinceWorkout ?? 9999,
  daysSinceMessage: (c) => c.daysSinceMessage ?? 9999,
  weeklyReports:    (c) => c.weeklyReports    ?? 0,
  engagementScore:  (c) => c.engagementScore  ?? 0,
};

const ClientsModule = ({ organizationId }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [filterCoach,    setFilterCoach]    = useState("all");
  const [filterTier,     setFilterTier]     = useState("all");
  const [filterFlag,     setFilterFlag]     = useState("all");
  const [searchName,     setSearchName]     = useState("");
  const [sortField,      setSortField]      = useState("performanceScore");
  const [sortDir,        setSortDir]        = useState("desc");

  const { data, isLoading, error } = useQuery({
    queryKey: ["dashboard-clients", organizationId],
    queryFn:  () => dashboardService.getClientMetrics(organizationId, true),
    staleTime:            60 * 60 * 1000,
    gcTime:               4 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: !!organizationId,
  });

  const clientData    = data || {};
  const summary       = clientData.summary       || {};
  const roster        = clientData.roster        || [];
  const needsAttention = clientData.needsAttention || [];

  // Unique coaches for filter dropdown
  const coaches = useMemo(() => {
    const seen = new Set();
    return roster
      .filter(c => c.coachFirstName && seen.size !== seen.add(c.coachId).size - 1 ? false : !seen.has(c.coachId) ? (seen.add(c.coachId), true) : false)
      // simpler approach:
      .reduce((acc, c) => {
        if (c.coachId && !acc.find(x => x.id === c.coachId)) {
          acc.push({ id: c.coachId, name: `${c.coachFirstName || ""} ${c.coachLastName || ""}`.trim() });
        }
        return acc;
      }, []);
  }, [roster]);

  // Filtered + sorted roster
  const filtered = useMemo(() => {
    let list = [...roster];

    if (searchName)        list = list.filter(c => `${c.firstName} ${c.lastName}`.toLowerCase().includes(searchName.toLowerCase()));
    if (filterCoach !== "all") list = list.filter(c => c.coachId === filterCoach);
    if (filterTier  !== "all") list = list.filter(c => c.performanceTier === filterTier);
    if (filterFlag  === "attention") list = list.filter(c => c.flags.noWorkout || c.flags.noMessage || c.flags.lowReports || c.flags.noProtocol);
    if (filterFlag  === "noWorkout") list = list.filter(c => c.flags.noWorkout);
    if (filterFlag  === "noMessage") list = list.filter(c => c.flags.noMessage);
    if (filterFlag  === "noProtocol") list = list.filter(c => c.flags.noProtocol);

    const getter = SORT_FIELDS[sortField] || SORT_FIELDS.performanceScore;
    list.sort((a, b) => {
      const av = getter(a), bv = getter(b);
      return sortDir === "asc" ? (av < bv ? -1 : av > bv ? 1 : 0) : (bv < av ? -1 : bv > av ? 1 : 0);
    });

    return list;
  }, [roster, searchName, filterCoach, filterTier, filterFlag, sortField, sortDir]);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir("desc"); }
  };

  const totalActive = summary.totalActive || 0;

  return (
    <Box sx={{ p: 0 }}>
      {/* KPI summary row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={2}>
          <MetricCard title="Total Active"     value={summary.totalActive}          icon={<PeopleIcon       sx={{ fontSize: 24 }} />} loading={isLoading} variant="default" />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <MetricCard title="Excellent"        value={summary.tierExcellent}        icon={<StarIcon         sx={{ fontSize: 24 }} />} loading={isLoading} variant="success" />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <MetricCard title="Good"             value={summary.tierGood}             icon={<CheckIcon        sx={{ fontSize: 24 }} />} loading={isLoading} variant="success" />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <MetricCard title="Needs Attention"  value={needsAttention.length}        icon={<WarningIcon      sx={{ fontSize: 24 }} />} loading={isLoading} variant={needsAttention.length > 0 ? "warning" : "default"} subtitle="flagged clients" />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <MetricCard title="Avg Completion"   value={summary.avgCompletionPct}     icon={<FitnessCenterIcon sx={{ fontSize: 24 }} />} loading={isLoading} format="percentage" />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <MetricCard title="Avg Perf Score"   value={summary.avgPerformanceScore}  icon={<StarIcon         sx={{ fontSize: 24 }} />} loading={isLoading} />
        </Grid>
      </Grid>

      {/* Needs attention alert strip */}
      {!isLoading && needsAttention.length > 0 && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          icon={<WarningIcon />}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {needsAttention.length} client{needsAttention.length !== 1 ? "s" : ""} need{needsAttention.length === 1 ? "s" : ""} attention —
            {" "}{needsAttention.filter(c => c.flags.noWorkout).length} no recent workout,
            {" "}{needsAttention.filter(c => c.flags.noMessage).length} no recent message,
            {" "}{needsAttention.filter(c => c.flags.noProtocol).length} missing protocol.
            {" "}Use the filter below to view them.
          </Typography>
        </Alert>
      )}

      {/* Filter bar */}
      <Card sx={{ mb: 3, boxShadow: theme.shadows[1] }}>
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                label="Search by name"
                size="small"
                fullWidth
                value={searchName}
                onChange={e => setSearchName(e.target.value)}
              />
            </Grid>
            {coaches.length > 1 && (
              <Grid item xs={12} sm={4} md={2}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Coach</InputLabel>
                  <Select value={filterCoach} label="Coach" onChange={e => setFilterCoach(e.target.value)}>
                    <MenuItem value="all">All coaches</MenuItem>
                    {coaches.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} sm={4} md={2}>
              <FormControl size="small" fullWidth>
                <InputLabel>Performance tier</InputLabel>
                <Select value={filterTier} label="Performance tier" onChange={e => setFilterTier(e.target.value)}>
                  <MenuItem value="all">All tiers</MenuItem>
                  <MenuItem value="Excellent">Excellent</MenuItem>
                  <MenuItem value="Good">Good</MenuItem>
                  <MenuItem value="Average">Average</MenuItem>
                  <MenuItem value="Needs Improvement">Needs Improvement</MenuItem>
                  <MenuItem value="No Progress Data">No Progress Data</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <FormControl size="small" fullWidth>
                <InputLabel>Flags</InputLabel>
                <Select value={filterFlag} label="Flags" onChange={e => setFilterFlag(e.target.value)}>
                  <MenuItem value="all">All clients</MenuItem>
                  <MenuItem value="attention">Any flag</MenuItem>
                  <MenuItem value="noWorkout">No workout 14d+</MenuItem>
                  <MenuItem value="noMessage">No message 7d+</MenuItem>
                  <MenuItem value="noProtocol">No protocol</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item>
              <Typography variant="caption" color="text.secondary">
                Showing {filtered.length} of {totalActive} clients
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Client roster table */}
      <Card sx={{ boxShadow: theme.shadows[2] }}>
        <TableContainer component={Paper} elevation={0}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, minWidth: 160 }}>
                  <TableSortLabel active={sortField === "name"} direction={sortField === "name" ? sortDir : "asc"} onClick={() => handleSort("name")}>
                    Client
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Coach</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Tier</TableCell>
                <TableCell sx={{ fontWeight: 600, minWidth: 80 }}>
                  <TableSortLabel active={sortField === "completionPct"} direction={sortField === "completionPct" ? sortDir : "asc"} onClick={() => handleSort("completionPct")}>
                    Completion
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, minWidth: 80 }}>
                  <TableSortLabel active={sortField === "performanceScore"} direction={sortField === "performanceScore" ? sortDir : "asc"} onClick={() => handleSort("performanceScore")}>
                    Score
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, minWidth: 90 }}>
                  <TableSortLabel active={sortField === "daysSinceWorkout"} direction={sortField === "daysSinceWorkout" ? sortDir : "asc"} onClick={() => handleSort("daysSinceWorkout")}>
                    Last Workout
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, minWidth: 90 }}>
                  <TableSortLabel active={sortField === "weeklyReports"} direction={sortField === "weeklyReports" ? sortDir : "asc"} onClick={() => handleSort("weeklyReports")}>
                    Reports (90d)
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, minWidth: 90 }}>
                  <TableSortLabel active={sortField === "daysSinceMessage"} direction={sortField === "daysSinceMessage" ? sortDir : "asc"} onClick={() => handleSort("daysSinceMessage")}>
                    Last Message
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>Flags</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 56 }} align="center">
                  Progress
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading
                ? Array.from({ length: 6 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 10 }).map((__, j) => (
                        <TableCell key={j}><Box sx={{ height: 16, bgcolor: "action.hover", borderRadius: 1 }} /></TableCell>
                      ))}
                    </TableRow>
                  ))
                : filtered.map(client => (
                    <TableRow
                      key={client.clientId}
                      hover
                      sx={{
                        bgcolor: (client.flags.noWorkout || client.flags.noProtocol)
                          ? `${theme.palette.warning.main}08`
                          : "inherit",
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {client.firstName} {client.lastName}
                        </Typography>
                        <Chip
                          label={client.riskCategory?.replace(/_/g, " ")}
                          size="small"
                          color={RISK_COLOR[client.riskCategory] || "default"}
                          variant="outlined"
                          sx={{ fontSize: "0.6rem", height: 18, mt: 0.25 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {client.coachFirstName || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <TierChip tier={client.performanceTier} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: client.completionPct >= 75 ? theme.palette.success.main : client.completionPct >= 50 ? theme.palette.warning.main : theme.palette.error.main }}>
                          {client.completionPct}%
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {client.performanceScore > 0 ? Math.round(client.performanceScore) : "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {client.daysSinceWorkout == null ? (
                          <Typography variant="body2" color="text.secondary">Never</Typography>
                        ) : (
                          <Typography variant="body2" sx={{ color: client.daysSinceWorkout > 14 ? theme.palette.error.main : client.daysSinceWorkout > 7 ? theme.palette.warning.main : "inherit" }}>
                            {client.daysSinceWorkout === 0 ? "Today" : `${client.daysSinceWorkout}d ago`}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: client.weeklyReports < 2 ? theme.palette.warning.main : "inherit" }}>
                          {client.weeklyReports}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {client.daysSinceMessage == null ? (
                          <Typography variant="body2" color="text.secondary">Never</Typography>
                        ) : (
                          <Typography variant="body2" sx={{ color: client.daysSinceMessage > 7 ? theme.palette.warning.main : "inherit" }}>
                            {client.daysSinceMessage === 0 ? "Today" : `${client.daysSinceMessage}d ago`}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <FlagBadges flags={client.flags} />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Progress over time">
                          <IconButton
                            size="small"
                            color="secondary"
                            aria-label="Open client progress"
                            onClick={() => {
                              navigate(
                                `/home/clients/progress/${client.clientId}/${organizationId}`,
                              );
                            }}
                          >
                            <ShowChartIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
              }
              {!isLoading && filtered.length === 0 && (
                  <TableRow>
                  <TableCell colSpan={10} sx={{ textAlign: "center", py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No clients match the current filters
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          Failed to load client data. Please refresh the page.
        </Alert>
      )}
    </Box>
  );
};

export default ClientsModule;
